import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Cloud, CloudUpload, RefreshCw, ShieldCheck, AlertCircle } from 'lucide-react';
import { getDb, type DNRDatabase } from '@data/db/database';
import { currentDeviceId, ensureDevice, resetDeviceId } from '@data/sync/deviceIdentity';
import { restoreCloudBusiness } from '@data/sync/cloudRestore';
import { repairGate, repairSideEffects, repairVerdict, runSummary } from './syncRepair';
import {
  describeRepublishBlockers,
  formatRepublishPlan,
  executeRepublish,
  previewRepublish,
  RepublishBlockedError,
  type RepublishPlan,
} from '@data/sync/republishEntities';
import {
  SUPPORT_REPUBLISH_BUSINESS_ID,
  SUPPORT_REPUBLISH_NOTE,
  SUPPORT_REPUBLISH_TARGETS,
} from './supportRepublish';
import {
  executeLocalEvidenceRecovery,
  formatLocalEvidenceRecoveryPlan,
  previewLocalEvidenceRecovery,
  type LocalEvidenceRecoveryPlan,
} from '@data/sync/localEvidenceRecovery';
import { resolveDiskBackupTarget } from '@data/backup/resolveDiskBackupTarget';
import {
  claimSupabaseBusiness,
  listSupabaseDevices,
  registerSupabaseDevice,
  revokeSupabaseDevice,
  supabaseCloudStatus,
  type RemoteDevice,
} from '@data/sync/supabaseSync';
import type { SyncOutboxEntry } from '@data/db/types';
import { failedEntries as getFailedEntries, retryFailed } from '@data/sync/outbox';
import { getSyncEngine, getSyncProvider, setSyncProvider } from '@data/sync/syncConfig';
import type { SyncOrigin } from '@data/sync/SyncEngine';
import { tableNameFor } from '@data/sync/applyChanges';
import { keepLocalVersion } from '@data/sync/resolveConflict';
import type { SyncConflict } from '@domain/sync/conflictModel';
import { explainStoredConflict, KEEP_LOCAL_ACKNOWLEDGEMENT_NOTICE, statusOfRecord } from '@domain/sync/conflictRecovery';
import type { SyncStatus } from '@domain/sync/retention';
import { useActiveBusiness, useBusinesses } from '@features/documents/useDocuments';
import { useAuth } from '@platform/auth/authContext';
import { Button } from '@ui/components/Button';
import { PageHeader } from '@ui/components/PageHeader';

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

interface StaleReview {
  readonly conflictId: string;
  readonly currentLocalVersion: number;
  readonly message: string;
}

interface OpenConflicts {
  readonly conflicts: SyncConflict[];
  readonly statuses: Record<string, string | undefined>;
}

/**
 * Open conflicts for a business, plus the CURRENT status of each local record.
 *
 * The status is read here rather than stored on the conflict row because the row
 * moves: a conflict recorded against a draft must stop calling it a draft the
 * moment that draft is issued. One read per conflict, on a screen that already
 * issues several queries, is the honest cost of a sentence that stays true.
 */
async function loadOpenConflicts(db: DNRDatabase, businessId: string): Promise<OpenConflicts> {
  const conflicts = await db.syncConflicts
    .where('businessId')
    .equals(businessId)
    .filter((item) => !item.resolved)
    .toArray();

  const statuses: Record<string, string | undefined> = {};
  await Promise.all(
    conflicts.map(async (conflict) => {
      const tableName = tableNameFor(conflict.entityType);
      if (!tableName) return;
      statuses[conflict.conflictId] = statusOfRecord(
        await db.table(tableName).get(conflict.entityId),
      );
    }),
  );

  return { conflicts, statuses };
}

export function SyncSettingsScreen() {
  const db = getDb();
  const { user } = useAuth();
  const { data: activeBusinessId } = useActiveBusiness();
  const { data: businesses = [] } = useBusinesses();
  const business = businesses.find((item) => item.id === activeBusinessId);
  const [configuredProvider, setConfiguredProvider] = useState<string | null>(null);
  const [providerStatus, setProviderStatus] =
    useState<Awaited<ReturnType<typeof supabaseCloudStatus>>>();
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [devices, setDevices] = useState<RemoteDevice[]>([]);
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);
  const [localDeviceId, setLocalDeviceId] = useState<string | null>(null);
  const [deviceLabel, setDeviceLabel] = useState('This device');
  const [error, setError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<string | null>(null);
  /** The last "Refresh status" confirmation. Deliberately separate from
   *  `lastRun`: reloading a panel is not a sync and must never read as one. */
  const [statusNotice, setStatusNotice] = useState<string | null>(null);
  const [failedOutbox, setFailedOutbox] = useState<SyncOutboxEntry[] | null>(null);
  const [busy, setBusy] = useState<'enable' | 'sync' | 'retry' | string | null>(null);
  const [conflictNotice, setConflictNotice] = useState<string | null>(null);
  const [conflictStatuses, setConflictStatuses] = useState<Record<string, string | undefined>>(
    {},
  );
  /**
   * A refusal waiting for the explicit second confirmation, for one conflict.
   *
   * Held per conflict rather than globally: the person is being asked about a
   * specific record, and a prompt attached to nothing is a prompt they cannot
   * answer.
   */
  const [revision, setRevision] = useState<StaleReview | null>(null);
  /**
   * The recovery plan the operator has SEEN but not yet confirmed.
   *
   * Held here rather than recomputed on click: the confirmation must be about
   * the plan that was displayed. `executeRepublish` re-derives and compares it
   * by fingerprint, so a record that moved in between is refused rather than
   * written as something the operator never read.
   */
  const [supportPlan, setSupportPlan] = useState<RepublishPlan | null>(null);
  const [supportReport, setSupportReport] = useState<string | null>(null);
  const [localEvidencePlan, setLocalEvidencePlan] = useState<LocalEvidenceRecoveryPlan | null>(null);
  const [localEvidenceReport, setLocalEvidenceReport] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    const [cloud, configured] = await Promise.all([supabaseCloudStatus(), getSyncProvider(db)]);
    setProviderStatus(cloud);
    setConfiguredProvider(configured?.id ?? null);

    let nextSyncStatus: SyncStatus | null = null;
    if (activeBusinessId && configured?.id === 'supabase') {
      const engine = await getSyncEngine(db);
      if (engine) nextSyncStatus = await engine.status(activeBusinessId);
    }

    let nextFailed: SyncOutboxEntry[] = [];
    if (activeBusinessId) {
      nextFailed = await getFailedEntries(db, activeBusinessId);
    }
    setFailedOutbox(nextFailed.length > 0 ? nextFailed : null);

    if (activeBusinessId && cloud.authorisedBusinessIds.includes(activeBusinessId)) {
      const [remoteDevices, open] = await Promise.all([
        listSupabaseDevices(activeBusinessId),
        loadOpenConflicts(db, activeBusinessId),
      ]);
      setDevices(remoteDevices);
      setConflicts(open.conflicts);
      setConflictStatuses(open.statuses);
      setLocalDeviceId(await currentDeviceId(db));
      if (nextSyncStatus) {
        nextSyncStatus = { ...nextSyncStatus, unresolvedConflictCount: open.conflicts.length };
      }
    } else {
      setDevices([]);
      if (activeBusinessId) {
        const open = await loadOpenConflicts(db, activeBusinessId);
        setConflicts(open.conflicts);
        setConflictStatuses(open.statuses);
        if (nextSyncStatus) {
          nextSyncStatus = { ...nextSyncStatus, unresolvedConflictCount: open.conflicts.length };
        }
      } else {
        setConflicts([]);
        setConflictStatuses({});
      }
    }
    setSyncStatus(nextSyncStatus);
  }, [activeBusinessId, db]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        await refresh();
      } catch (caught) {
        if (active) setError(caught instanceof Error ? caught.message : String(caught));
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [refresh]);

  /**
   * One manual cycle, reported the same way from both sync buttons.
   *
   * The work is identical — the engine pushes, pulls, applies and only then
   * advances the cursor — and `mode` only names which control the person
   * pressed, so the guards and the busy label match it. A retry additionally
   * requeues the permanently failed entries first.
   */
  async function performSync(
    mode: 'sync' | 'retry',
    origin: SyncOrigin = 'manual',
  ): Promise<void> {
    if (!activeBusinessId) return;
    if (!user) {
      setError('Sign in to your DNR account before syncing.');
      return;
    }
    // Never sync a business that the authenticated user does not own in the
    // cloud. Switching Supabase accounts in the same browser can leave a
    // previous account's business selected locally; that must not be sent to
    // the cloud under the new user's credentials.
    if (!providerStatus?.authorisedBusinessIds.includes(activeBusinessId)) {
      setError(
        'This business is not linked to your DNR account. Sign out and sign in as the account that owns it, or restore it from the cloud.',
      );
      return;
    }
    setError(null);
    // A stale success must not survive the attempt that follows it.
    setLastRun(null);
    setStatusNotice(null);
    setBusy(mode);
    try {
      const engine = await getSyncEngine(db);
      if (!engine) throw new Error('Choose DNR Cloud Sync before running sync.');
      if (mode === 'retry') {
        // Resets the rejected entries (status AND attempt counter) so this run
        // actually re-sends them instead of tripping the same ceiling again.
        const retried = await retryFailed(db, activeBusinessId);
        if (retried === 0) {
          setError('No failed changes to retry.');
          return;
        }
      }
      const result = await engine.run(activeBusinessId, origin);

      // ORDER IS THE POINT, exactly as in `repairCloudCopy` below. `refresh()`
      // starts with `setError(null)`, so a message published before it is
      // erased in the same tick — which is why a failed manual sync used to
      // show no reason at all.
      try {
        await refresh();
      } catch {
        // A panel that cannot be reloaded must not swallow the run's outcome:
        // the run's result is the fact the person came here for.
      }

      setSyncStatus(result.status);
      setLastRun(runSummary(result));
      if (result.status.kind === 'error' && result.status.failedError) {
        setError(result.status.failedError);
      } else if (result.status.kind === 'error' && result.status.lastError) {
        setError(result.status.lastError);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Reload what this panel DISPLAYS. It is not a sync.
   *
   * No change is pushed, pulled, applied or acknowledged, and the cursor is not
   * touched. People ask "what is the state now?" far more often than they ask
   * "sync now", and before this the only way to get an answer was to leave the
   * screen and come back to it.
   */
  async function refreshStatus(): Promise<void> {
    if (busy) return;
    setError(null);
    setStatusNotice(null);
    setBusy('refresh');
    try {
      await refresh();
      setStatusNotice(`Status reloaded at ${new Date().toLocaleTimeString()}.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Acknowledge one conflict by keeping the version this device already has.
   *
   * Deliberately NOT wrapped in `acquireSyncLock`: `keepLocalVersion` takes that
   * lock itself, and it must not call `engine.run()` either — the lock is not
   * re-entrant, so a resolution that started a sync would wait forever on itself.
   * Nothing is pushed, pulled or re-cursored by this action.
   *
   * `reviewedLocalVersion` is passed ONLY after the person has been shown the
   * version now on the record (the second confirmation below). It names what they
   * saw; the service still checks it against the row.
   */
  async function keepLocal(conflict: SyncConflict, reviewedLocalVersion?: number): Promise<void> {
    setError(null);
    setConflictNotice(null);
    setBusy(`conflict:${conflict.conflictId}`);
    try {
      const result = await keepLocalVersion(db, conflict.conflictId, {
        actor: user ? { userId: user.id, userName: user.email ?? 'You' } : null,
        reviewedLocalVersion,
      });
      if (!result.ok) {
        if (result.code === 'STALE_LOCAL' && result.currentLocalVersion !== undefined) {
          // Not a dead end. The record moved on after the conflict was recorded, so
          // the acknowledgement has to name the version the person is now looking
          // at — asked for once, next to the record, instead of leaving a warning
          // that nothing can ever clear.
          setRevision({
            conflictId: conflict.conflictId,
            currentLocalVersion: result.currentLocalVersion,
            message: result.message,
          });
          return;
        }
        setError(result.message);
        return;
      }
      setRevision(null);
      setConflictNotice(
        result.alreadyResolved
          ? 'This conflict had already been acknowledged; nothing changed.'
          : KEEP_LOCAL_ACKNOWLEDGEMENT_NOTICE,
      );
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Repair the cloud copy of an already-local business.
   *
   * This is `restoreCloudBusiness`'s already-local branch with its outcome
   * actually read. Recovery decides, the engine uploads, and the result is
   * verified against the cloud before anything is called a success.
   *
   * Deliberately NOT wrapped in `acquireSyncLock`: `SyncEngine.run()` acquires
   * that lock itself and it is not re-entrant, so a wrapper here would wait
   * forever on a lock nobody else can release.
   */
  async function repairCloudCopy(): Promise<void> {
    const gate = repairGate({
      businessId: activeBusinessId,
      busy,
      signedIn: Boolean(user),
      authorised: Boolean(
        activeBusinessId && providerStatus?.authorisedBusinessIds.includes(activeBusinessId),
      ),
    });
    if (gate === 'busy' || gate === 'no_business') return;
    if (gate === 'signed_out') {
      setError('Sign in to your DNR account before repairing the cloud copy.');
      return;
    }
    if (gate === 'not_authorised') {
      setError(
        'This business is not linked to your DNR account. Sign out and sign in as the account that owns it.',
      );
      return;
    }
    if (!activeBusinessId) return;

    setError(null);
    setLastRun(null);
    setBusy('repair');
    try {
      const result = await restoreCloudBusiness(db, activeBusinessId);
      const verdict = repairVerdict(result.repair);
      // Whatever else the cycle moved, say so — the engine drains the whole
      // outbox and applies the whole pull, so a repair is never only one row.
      const side = result.repair ? repairSideEffects(result.repair) : '';

      // ORDER IS THE POINT. `refresh()` reloads this panel and its first act is
      // `setError(null)`, so it must finish BEFORE the verdict is published.
      // Publishing first — as this handler used to — set the message and then
      // erased it in the same tick, which made a refused or failed repair look
      // like nothing had happened while a success survived in `lastRun`.
      try {
        await refresh();
      } catch {
        // A panel that cannot be reloaded must not swallow the verdict: the
        // repair outcome is the fact the user came here for.
      }

      switch (verdict.kind) {
        case 'refused':
          setError(`Repair refused (${verdict.code}): ${verdict.message}`);
          break;
        case 'push_failed':
          setError(
            `Could not upload the business: ${verdict.message}${side} Use "Retry failed changes", then repair again.`,
          );
          break;
        case 'deferred':
          // Not an error and not a success: the entry is still queued, so the
          // next cycle uploads it without any manual reset.
          setError(
            `The upload was deferred, so the cloud does not have the business row yet.${side} Use Sync now, then repair again.`,
          );
          break;
        case 'unverified':
          setError(`${verdict.message}${side}`);
          break;
        case 'still_missing':
          setError(
            `The cloud still has no business row after the upload.${side} Use Sync now, then repair again. If it persists, contact support.`,
          );
          break;
        case 'repaired':
          setLastRun(`Business republished to the cloud and verified.${side}`);
          break;
        case 'already_present':
          setLastRun(`The cloud already holds this business — nothing to repair.${side}`);
          break;
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Read-only preview of the Krishna recovery.
   *
   * Its whole contract is that pressing it changes NOTHING: it reads this
   * device's rows and outbox, resolves each record's already-recorded version,
   * and prints the result. Only the button beside it writes.
   */
  async function previewSupportRepublish(): Promise<void> {
    if (activeBusinessId !== SUPPORT_REPUBLISH_BUSINESS_ID) return;
    setError(null);
    setSupportPlan(null);
    setSupportReport(null);
    setBusy('support-preview');
    try {
      const plan = await previewRepublish(db, {
        businessId: SUPPORT_REPUBLISH_BUSINESS_ID,
        targets: SUPPORT_REPUBLISH_TARGETS,
        now: new Date().toISOString(),
      });
      setSupportPlan(plan.blockers.length === 0 ? plan : null);
      setSupportReport(formatRepublishPlan(plan));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Queue the previewed recovery. Local writes only — no provider is touched.
   */
  async function executeSupportRepublish(): Promise<void> {
    if (activeBusinessId !== SUPPORT_REPUBLISH_BUSINESS_ID) return;
    if (!supportPlan) {
      setError('Run the read-only preview first; it is what establishes each record\'s version.');
      return;
    }
    const confirmed = window.confirm(
      `Queue ${supportPlan.items.length} recovery change(s) at the versions already recorded for them?\n\n` +
        'This writes to this device only, and nothing is uploaded until you press Sync now.',
    );
    if (!confirmed) return;

    setError(null);
    setBusy('support-republish');
    try {
      const result = await executeRepublish(db, supportPlan, { now: new Date().toISOString() });
      setSupportPlan(null);
      try {
        await refresh();
      } catch {
        // The queued changes are the fact that matters; a panel that cannot be
        // reloaded must not hide them.
      }
      setSupportReport(
        [
          `Queued ${result.itemCount} record(s) as ${result.changeIds.length} change(s):`,
          ...result.changeIds.map((changeId) => `  ${changeId}`),
          '',
          'Nothing has been uploaded. These are pending on this device; press Sync now when you ' +
            'are ready for the other device to receive them.',
        ].join('\n'),
      );
    } catch (caught) {
      if (caught instanceof RepublishBlockedError) {
        setSupportPlan(null);
        setSupportReport(
          ['REFUSED — nothing was written:', describeRepublishBlockers(caught.blockers)].join('\n'),
        );
      } else {
        setError(caught instanceof Error ? caught.message : String(caught));
      }
    } finally {
      setBusy(null);
    }
  }

  async function previewLocalEvidence(): Promise<void> {
    if (activeBusinessId !== SUPPORT_REPUBLISH_BUSINESS_ID) return;
    setError(null);
    setLocalEvidencePlan(null);
    setLocalEvidenceReport(null);
    setBusy('local-evidence-preview');
    try {
      const resolved = resolveDiskBackupTarget();
      if (resolved.platform !== 'browser') {
        throw new Error('Local-evidence recovery must be reviewed in the original Edge browser profile.');
      }
      const plan = await previewLocalEvidenceRecovery(db, resolved.target);
      setLocalEvidencePlan(plan.eligible ? plan : null);
      setLocalEvidenceReport(formatLocalEvidenceRecoveryPlan(plan));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  }

  async function executeLocalEvidence(): Promise<void> {
    if (activeBusinessId !== SUPPORT_REPUBLISH_BUSINESS_ID) return;
    if (!localEvidencePlan?.eligible) {
      setError('Run a fully eligible local-evidence preview before approving recovery.');
      return;
    }
    const confirmed = window.confirm(
      `Queue exactly ${localEvidencePlan.items.length} recovery events from preserved local Edge evidence?\n\n` +
        'The original cloud acknowledgement is UNPROVEN. This will only queue new local outbox events; ' +
        'it will not push, pull, restore, or alter the original backup. Review the complete preview before continuing.',
    );
    if (!confirmed) return;

    setError(null);
    setBusy('local-evidence-recovery');
    try {
      const resolved = resolveDiskBackupTarget();
      if (resolved.platform !== 'browser') {
        throw new Error('Local-evidence recovery must be executed in the original Edge browser profile.');
      }
      const result = await executeLocalEvidenceRecovery(
        db,
        resolved.target,
        localEvidencePlan,
        new Date().toISOString(),
      );
      setLocalEvidencePlan(null);
      setLocalEvidenceReport(
        [
          `Queued ${result.itemCount} local-evidence recovery events:`,
          ...result.changeIds.map((changeId) => `  ${changeId}`),
          '',
          'These are new events, not proven or reused original cloud events. Nothing was uploaded. ' +
            'They will travel only through the normal sync push when separately initiated.',
        ].join('\n'),
      );
    } catch (caught) {
      setLocalEvidencePlan(null);
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  }

  async function enableSync(): Promise<void> {
    if (!activeBusinessId) return;
    if (!user) {
      setError('Sign in to your DNR account before enabling cloud sync.');
      return;
    }
    setError(null);
    setBusy('enable');
    try {
      await claimSupabaseBusiness(activeBusinessId);
      const localDevice = await ensureDevice(db, activeBusinessId);
      let deviceId = await currentDeviceId(db);
      const label = deviceLabel.trim() || 'This device';
      await db.devices.put({ ...localDevice, label });
      try {
        await registerSupabaseDevice({
          businessId: activeBusinessId,
          deviceId,
          label,
          platform: localDevice.platform ?? 'unknown',
        });
      } catch (registrationError) {
        // If the device was revoked on the server, generate a new device ID and retry
        const message =
          registrationError instanceof Error
            ? registrationError.message
            : String(registrationError);
        if (/revoked/i.test(message)) {
          deviceId = await resetDeviceId(db);
          await registerSupabaseDevice({
            businessId: activeBusinessId,
            deviceId,
            label,
            platform: localDevice.platform ?? 'unknown',
          });
        } else {
          throw registrationError;
        }
      }
      await setSyncProvider(db, 'supabase');
      setConfiguredProvider('supabase');
      await performSync('sync', 'initial_enable');
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  }

  async function disableOnThisDevice(): Promise<void> {
    setError(null);
    try {
      await setSyncProvider(db, null);
      setConfiguredProvider(null);
      setSyncStatus(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }

  async function revoke(device: RemoteDevice): Promise<void> {
    if (!activeBusinessId || device.deviceId === (await currentDeviceId(db))) return;
    if (
      !window.confirm(
        `Revoke sync access for "${device.label}"? That device will no longer be able to sync.`,
      )
    ) {
      return;
    }
    setError(null);
    setBusy(device.deviceId);
    try {
      await revokeSupabaseDevice(activeBusinessId, device.deviceId);
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  }

  const isOwner = Boolean(
    activeBusinessId && providerStatus?.authorisedBusinessIds.includes(activeBusinessId),
  );
  /**
   * The temporary recovery panel. One business, no override, no "select a
   * business" affordance: a control that can be pointed at any business is a
   * control somebody will point at the wrong one.
   */
   const supportRecovery = activeBusinessId === SUPPORT_REPUBLISH_BUSINESS_ID;
   const isConfigured = configuredProvider === 'supabase';
  const canEnableSync = isOwner && !isConfigured && user && providerStatus?.state === 'connected';
  const hasActiveBusinessNotSynced =
    activeBusinessId && !isOwner && providerStatus?.state === 'connected';
  const canClaimBusiness = hasActiveBusinessNotSynced && !isConfigured && user;
  /** A failed last cycle changes what the primary action means to the person. */
  const syncFailed = syncStatus?.kind === 'error';

  return (
    <section data-testid="sync-settings-screen">
      <PageHeader
        title="Sync & Cloud"
        description="Keep this business in sync across devices while continuing to work offline."
        action={
          isConfigured ? (
            <Button
              variant="primary"
              onClick={() => void performSync('sync')}
              loading={busy === 'sync'}
              loadingText={syncFailed ? 'Retrying…' : 'Syncing…'}
              // A retry or a repair already holds the engine's lock. Letting a
              // third run queue behind them would hide the wait, not remove it.
              disabled={busy !== null && busy !== 'sync'}
              icon={<RefreshCw className="h-4 w-4" aria-hidden />}
            >
              {syncFailed ? 'Retry sync' : 'Sync now'}
            </Button>
          ) : undefined
        }
      />

      <div className="space-y-4">
        {!business ? (
          <section className="rounded-lg border border-slate-800 p-4 text-sm text-slate-400">
            Create or select a business before enabling cloud sync.
          </section>
        ) : (
          <section className="space-y-3 rounded-lg border border-slate-800 p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-100">
              <Cloud className="h-4 w-4 text-sky-400" aria-hidden />
              DNR Cloud Sync
            </h2>
            <p className="text-sm text-slate-400">
              Business: <span className="text-slate-200">{business.legalName}</span>
            </p>
            <p className="text-sm text-slate-400">
              Account:{' '}
              <span className="text-slate-200">
                {providerStatus?.accountLabel ?? 'Not signed in'}
              </span>
            </p>
            <p className="text-sm text-slate-400">
              Cloud status:{' '}
              <span className="text-slate-200">
                {providerStatus?.state ?? 'Checking'}
                {providerStatus?.message ? ` — ${providerStatus.message}` : ''}
              </span>
            </p>
            {isConfigured && syncStatus ? (
              <div className="rounded border border-slate-800 bg-slate-900/60 p-3 text-sm">
                <p className="font-medium text-slate-200">
                  Sync state: {syncStatus.kind}
                  {syncStatus.pendingCount ? ` · ${syncStatus.pendingCount} pending` : ''}
                  {syncStatus.failedCount ? ` · ${syncStatus.failedCount} failed` : ''}
                  {syncStatus.unresolvedConflictCount
                    ? ` · ${syncStatus.unresolvedConflictCount} unresolved conflict(s)`
                    : ''}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Last successful sync: {formatDate(syncStatus.lastSuccessfulAt)}
                  {syncStatus.lastSyncOrigin === 'automatic' ? ' (auto)' : ''}
                </p>
                {syncStatus.lastError ? (
                  <p className="mt-1 break-all text-xs text-red-300/90">
                    Last sync error: {syncStatus.lastError}
                  </p>
                ) : null}
                {syncStatus.failedCount > 0 ? (
                  <div className="mt-2 rounded border border-red-900/50 bg-red-950/30 p-2 text-xs text-red-200">
                    <p className="font-medium">
                      {syncStatus.failedCount} change(s) could not be uploaded to the cloud.
                    </p>
                    {syncStatus.failedError ? (
                      <p className="mt-1 break-all text-red-300/80">{syncStatus.failedError}</p>
                    ) : null}
                    <p className="mt-1">
                      Local data is safe. Use "Retry failed changes" to attempt them again — only
                      do so if the underlying issue has been resolved.
                    </p>
                  </div>
                ) : null}
                {syncStatus.skippedChangeCount ? (
                  <div className="mt-2 rounded border border-amber-900/50 bg-amber-950/30 p-2 text-xs text-amber-200">
                    <p className="font-medium">
                      {syncStatus.skippedChangeCount} change(s) in the cloud could not be read by
                      this device.
                    </p>
                    {syncStatus.skippedChangeReasons?.length ? (
                      <p className="mt-1 break-all text-amber-300/80">
                        {syncStatus.skippedChangeReasons.join(', ')}
                      </p>
                    ) : null}
                    <p className="mt-1">
                      Everything else was synced. These records were left untouched rather than
                      guessed at, and will be skipped again until they are corrected in the cloud.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
            {isConfigured ? (
              <div className="space-y-1 border-t border-slate-800 pt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void refreshStatus()}
                  loading={busy === 'refresh'}
                  loadingText="Refreshing…"
                  disabled={busy !== null && busy !== 'refresh'}
                  icon={<RefreshCw className="h-4 w-4" aria-hidden />}
                >
                  Refresh status
                </Button>
                <p className="text-xs text-slate-500">
                  Reloads this panel from this device and your DNR account. It does not upload or
                  apply any change — use {syncFailed ? 'Retry sync' : 'Sync now'} for that.
                </p>
                {statusNotice ? (
                  <p role="status" className="text-xs text-slate-400">
                    {statusNotice}
                  </p>
                ) : null}
              </div>
            ) : null}
            {isConfigured && isOwner && user ? (
              <div className="space-y-2 border-t border-slate-800 pt-3">
                <p className="text-xs text-slate-500">
                  Repair re-publishes this business under its original change id, and only after
                  the cloud confirms the business row is absent. It runs a full sync cycle, so any
                  other pending changes are uploaded and new cloud changes are applied as well.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void repairCloudCopy()}
                  loading={busy === 'repair'}
                  loadingText="Repairing…"
                  disabled={busy !== null && busy !== 'repair'}
                  icon={<CloudUpload className="h-4 w-4" aria-hidden />}
                >
                  Repair cloud copy
                </Button>
              </div>
            ) : null}
            {supportRecovery ? (
              <div className="space-y-2 rounded-lg border border-amber-800 bg-amber-950/30 p-3">
                <p className="text-sm font-medium text-amber-200">
                  Support recovery — {SUPPORT_REPUBLISH_TARGETS.length} records missing on another
                  device
                </p>
                <p className="text-xs text-amber-300">{SUPPORT_REPUBLISH_NOTE}</p>
                <p className="text-xs text-amber-300/90">
                  Preview is read-only. Republishing queues one change per record at the version
                  already recorded for it, parents first, and writes nothing to the cloud — the
                  records travel only when you press "Sync now" yourself. A completed recovery is
                  recorded durably, so it cannot be run twice.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void previewSupportRepublish()}
                    loading={busy === 'support-preview'}
                    loadingText="Reading…"
                    disabled={busy !== null && busy !== 'support-preview'}
                  >
                    Preview recovery
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => void executeSupportRepublish()}
                    loading={busy === 'support-republish'}
                    loadingText="Queuing…"
                    disabled={
                      !supportPlan || (busy !== null && busy !== 'support-republish')
                    }
                  >
                    Republish {SUPPORT_REPUBLISH_TARGETS.length} records
                  </Button>
                </div>
                {supportReport ? (
                  <div className="space-y-1 rounded bg-slate-900/70 p-2 text-[11px] leading-relaxed text-slate-300">
                    <p className="whitespace-pre-wrap break-all font-mono">{supportReport}</p>
                  </div>
                ) : null}
                <div className="space-y-2 border-t border-amber-800 pt-3">
                  <p className="text-sm font-medium text-amber-200">
                    Recover from verified Edge backup
                  </p>
                  <p className="text-xs text-amber-300/90">
                    Separate from Republish. This uses preserved local evidence because original cloud
                    acknowledgement is unproven. Preview reads the saved backup with existing read
                    permission only; every record must pass source, schema, and relationship checks.
                    Recovery queues new local events only and never starts sync.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void previewLocalEvidence()}
                      loading={busy === 'local-evidence-preview'}
                      loadingText="Validating evidence…"
                      disabled={busy !== null && busy !== 'local-evidence-preview'}
                    >
                      Preview local-evidence recovery
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => void executeLocalEvidence()}
                      loading={busy === 'local-evidence-recovery'}
                      loadingText="Queuing locally…"
                      disabled={
                        !localEvidencePlan?.eligible ||
                        (busy !== null && busy !== 'local-evidence-recovery')
                      }
                    >
                      Approve and queue 8 recovery events
                    </Button>
                  </div>
                  {localEvidenceReport ? (
                    <div className="rounded bg-slate-900/70 p-2 text-[11px] leading-relaxed text-slate-300">
                      <p className="whitespace-pre-wrap break-all font-mono">{localEvidenceReport}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            {canEnableSync ? (
              <div className="space-y-3 border-t border-slate-800 pt-3">
                <p className="text-sm text-slate-300">
                  Sync is not yet enabled on this device. Other devices that have already been set
                  up are listed below — click "Enable Cloud Sync" to add this device to the sync
                  network.
                </p>
                <label className="block text-sm text-slate-300">
                  Name this device
                  <input
                    value={deviceLabel}
                    onChange={(event) => setDeviceLabel(event.target.value)}
                    maxLength={80}
                    className="mt-1 block w-full max-w-sm rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                  />
                </label>
                <Button
                  variant="primary"
                  onClick={() => void enableSync()}
                  loading={busy === 'enable'}
                  loadingText="Enabling…"
                >
                  Enable Cloud Sync
                </Button>
              </div>
            ) : null}
            {canClaimBusiness ? (
              <div className="space-y-3 border-t border-amber-800 pt-3 bg-amber-950/30 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" aria-hidden />
                  <div className="text-sm text-amber-200">
                    <p className="font-medium">This business is not yet synced to the cloud.</p>
                    <p className="mt-1 text-xs text-amber-300">
                      The DNR session is connected, but this business has not been claimed in the
                      cloud yet. Click "Enable Cloud Sync" to upload this business to your DNR
                      account.
                    </p>
                  </div>
                </div>
                <label className="block text-sm text-amber-200">
                  Name this device
                  <input
                    value={deviceLabel}
                    onChange={(event) => setDeviceLabel(event.target.value)}
                    maxLength={80}
                    className="mt-1 block w-full max-w-sm rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                  />
                </label>
                <Button
                  variant="primary"
                  onClick={() => void enableSync()}
                  loading={busy === 'enable'}
                  loadingText="Enabling…"
                >
                  Enable Cloud Sync
                </Button>
              </div>
            ) : null}
            {isConfigured ? (
              <Button variant="secondary" onClick={() => void disableOnThisDevice()}>
                Disable sync on this device
              </Button>
            ) : null}
            {isConfigured && failedOutbox && failedOutbox.length > 0 ? (
              <Button
                variant="secondary"
                onClick={() => void performSync('retry')}
                loading={busy === 'retry'}
                loadingText="Retrying…"
              >
                Retry failed changes
              </Button>
            ) : null}
            {isConfigured && failedOutbox && failedOutbox.length > 0 ? (
              <div className="mt-2 space-y-1">
                {failedOutbox.map((entry) => (
                  <div
                    key={entry.changeId}
                    className="rounded border border-red-900/50 bg-red-950/20 p-2 text-xs"
                  >
                    <p className="font-medium text-red-200">
                      {entry.entityType} · {entry.entityId}
                    </p>
                    <p className="mt-0.5 break-all text-red-300/80">
                      {entry.lastError ?? 'Unknown error'}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
            <p className="text-xs text-slate-500">
              The account that first enables sync owns this business in the cloud. Use that same DNR
              login on every device. Your local database remains usable offline; turning sync off
              here does not delete cloud history or local records.
            </p>
          </section>
        )}

        {isOwner ? (
          <section className="space-y-3 rounded-lg border border-slate-800 p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-100">
              <ShieldCheck className="h-4 w-4 text-emerald-400" aria-hidden />
              Devices
            </h2>
            {devices.length === 0 ? (
              <p className="text-sm text-slate-500">No devices have synced yet.</p>
            ) : (
              <ul className="divide-y divide-slate-800">
                {devices.map((device) => (
                  <li
                    key={device.deviceId}
                    className="flex flex-wrap items-center justify-between gap-3 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-200">
                        {device.label}
                        {device.revokedAt ? (
                          <span className="ml-2 text-xs text-red-400">Revoked</span>
                        ) : null}
                        {!device.revokedAt && device.deviceId === localDeviceId ? (
                          <span className="ml-2 text-xs text-slate-500">This device</span>
                        ) : null}
                      </p>
                      <p className="text-xs text-slate-500">
                        {device.platform} · Last seen {formatDate(device.lastSeenAt)}
                      </p>
                    </div>
                    {!device.revokedAt && device.deviceId !== localDeviceId ? (
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busy === device.deviceId}
                        onClick={() => void revoke(device)}
                      >
                        Revoke
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {conflicts.length > 0 ? (
          <section className="space-y-3 rounded-lg border border-amber-900/70 bg-amber-950/20 p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-200">
              <AlertTriangle className="h-4 w-4" aria-hidden />
              Conflicts need review ({conflicts.length})
            </h2>
            <p className="text-sm text-amber-100/80">
              No conflicting financial or stock record was silently overwritten. Review each record
              on both devices; correct issued documents with the normal credit-note or cancellation
              workflow.
            </p>
            <ul className="divide-y divide-amber-900/50">
              {conflicts.map((conflict) => {
                // "The other device" is only true when it IS another device. Two
                // device ids that agree mean this device recorded both versions,
                // and sending the user to look at a second machine wastes their time.
                const sameDevice = conflict.localDeviceId === conflict.remoteDeviceId;
                const actionKey = `conflict:${conflict.conflictId}`;
                return (
                  <li key={conflict.conflictId} className="space-y-2 py-3">
                    <p className="text-sm font-medium text-amber-100">
                      {conflict.entityType} · {conflict.entityId} · {conflict.reason}
                    </p>
                    <p className="mt-1 text-xs text-amber-100/70">
                      {explainStoredConflict(conflict, conflictStatuses[conflict.conflictId])}
                    </p>
                    <p className="mt-1 text-xs text-amber-100/50">
                      Conflict recorded against this device’s version {conflict.localVersion} and
                      incoming version {conflict.remoteVersion},{' '}
                      {sameDevice ? 'recorded by this same device' : 'from another device'}.
                      Detected {formatDate(conflict.detectedAt)}.
                      {conflict.pendingChangeOmitted === 'TOO_LARGE'
                        ? ' The refused change was too large to keep in full.'
                        : ''}
                    </p>
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={busy === actionKey}
                      loadingText="Keeping…"
                      onClick={() => void keepLocal(conflict)}
                    >
                      Keep this device’s version
                    </Button>
                    {revision && revision.conflictId === conflict.conflictId ? (
                      // The record moved on after the conflict was recorded. The
                      // acknowledgement is still available, but only for the version
                      // on screen NOW — so it is asked for once, here, rather than
                      // refused forever.
                      <div className="space-y-2 rounded border border-amber-800/60 p-2">
                        <p className="text-xs text-amber-100/80">{revision.message}</p>
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={busy === actionKey}
                          loadingText="Keeping…"
                          onClick={() => void keepLocal(conflict, revision.currentLocalVersion)}
                        >
                          Keep the version now on this device (v{revision.currentLocalVersion})
                        </Button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {/* Outside the panel on purpose: acknowledging the LAST conflict unmounts the
            panel, and a confirmation that disappears with it says nothing. */}
        {conflictNotice ? (
          <p role="status" className="text-sm text-slate-300">
            {conflictNotice}
          </p>
        ) : null}

        {lastRun ? (
          <p role="status" className="text-sm text-slate-300">
            {lastRun}
          </p>
        ) : null}
        {error ? (
          <p
            role="alert"
            className="rounded border border-red-900 bg-red-950/30 p-3 text-sm text-red-200"
          >
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}
